import React, { useState, useRef, useEffect } from "react";
import useTeamMembersAndTeams from "../utils/useTeamMembersAndTeams";

const AUDIENCE_OPTIONS = [
  { id: "EVERYONE", label: "Everyone", sub: null },
  { id: "SPECIFIC_USERS", label: "Members", sub: null },
  { id: "SPECIFIC_TEAMS", label: "Teams", sub: null }
];

const AudienceScopePopover = ({ widgetId, audienceScope, onClose, onSave }) => {
  const [useGlobal, setUseGlobal] = useState(!audienceScope?.type);
  const [mode, setMode] = useState(audienceScope?.type || "EVERYONE");
  const [selectedUsers, setSelectedUsers] = useState(audienceScope?.memberIds || []);
  const [selectedTeams, setSelectedTeams] = useState(audienceScope?.teamIds || []);
  const { members, teams, loading } = useTeamMembersAndTeams();
  const ref = useRef(null);

  useEffect(() => {
    const handler = e => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [onClose]);

  const toggleUser = id =>
    setSelectedUsers(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));
  const toggleTeam = id =>
    setSelectedTeams(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));

  const handleSave = () => {
    if (useGlobal) {
      onSave(null);
      onClose();
      return;
    }
    const scope =
      mode === "EVERYONE"
        ? { type: "EVERYONE", memberIds: [], teamIds: [] }
        : mode === "SPECIFIC_USERS"
          ? { type: "SPECIFIC_USERS", memberIds: selectedUsers, teamIds: [] }
          : { type: "SPECIFIC_TEAMS", memberIds: [], teamIds: selectedTeams };
    onSave(scope);
    onClose();
  };

  return (
    <div ref={ref} className="audienceScopePopover">
      <div
        className={`audienceScopeGlobalRow${useGlobal ? " audienceScopeGlobalRowActive" : ""}`}
        onClick={() => setUseGlobal(v => !v)}
      >
        <span className={`audienceScopeCheckbox${useGlobal ? " audienceScopeCheckboxChecked" : ""}`} />
        <p className="audienceScopeGlobalLabel">Use the dashboard's current member/team filter</p>
      </div>

      <div className={`audienceScopeScopeBlock${useGlobal ? " audienceScopeScopeBlockDisabled" : ""}`}>
        <p className="audienceScopePopoverTitle">Show data for</p>
        <div className="audienceScopeOptions">
          {AUDIENCE_OPTIONS.map(opt => (
            <React.Fragment key={opt.id}>
              <div
                className={`audienceScopeOption${mode === opt.id ? " audienceScopeOptionActive" : ""}`}
                onClick={() => !useGlobal && setMode(opt.id)}
              >
                <span className="audienceScopeOptionRadio" />
                <div>
                  <p className="audienceScopeOptionLabel">{opt.label}</p>
                  {opt.sub && <p className="audienceScopeOptionSub">{opt.sub}</p>}
                </div>
              </div>

              {opt.id === "SPECIFIC_USERS" && mode === "SPECIFIC_USERS" && (
                <div className="audienceScopeList">
                  {loading && <p className="audienceScopeLoading">Loading members…</p>}
                  {!loading &&
                    members.map(m => {
                      const id = m.memberId || m.id;
                      const checked = selectedUsers.includes(id);
                      return (
                        <div key={id} className="audienceScopeListRow" onClick={() => toggleUser(id)}>
                          <span className={`audienceScopeCheckbox${checked ? " audienceScopeCheckboxChecked" : ""}`} />
                          <span className="audienceScopeListLabel">{m.name || m.firstName || m.email || id}</span>
                        </div>
                      );
                    })}
                  {!loading && !members.length && <p className="audienceScopeEmpty">No members found</p>}
                </div>
              )}

              {opt.id === "SPECIFIC_TEAMS" && mode === "SPECIFIC_TEAMS" && (
                <div className="audienceScopeList">
                  {loading && <p className="audienceScopeLoading">Loading teams…</p>}
                  {!loading &&
                    teams.map(t => {
                      const id = t.id || t.teamGroupId;
                      const checked = selectedTeams.includes(id);
                      return (
                        <div key={id} className="audienceScopeListRow" onClick={() => toggleTeam(id)}>
                          <span className={`audienceScopeCheckbox${checked ? " audienceScopeCheckboxChecked" : ""}`} />
                          <span className="audienceScopeListLabel">{t.name || id}</span>
                        </div>
                      );
                    })}
                  {!loading && !teams.length && <p className="audienceScopeEmpty">No teams found</p>}
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      <div className="audienceScopeFooter">
        <button type="button" className="audienceScopeCancelBtn" onClick={onClose}>
          Cancel
        </button>
        <button type="button" className="audienceScopeSaveBtn" onClick={handleSave}>
          Save
        </button>
      </div>
    </div>
  );
};

export default AudienceScopePopover;
